import {
  Home,
  BookOpen,
  Target,
  User,
  Settings,
  Users,
  FolderTree,
  Mail,
  BarChart3,
  LogOut,
  Building2,
  GraduationCap,
  Trophy,
  KeyRound,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUser } from "@/contexts/UserContext";
import { Roles } from "@/shared/constants/roles";
import { authService } from "@/services/Auth/auth.service";
import axios from "axios";
import { toast } from "sonner";
import {
  changePasswordSchema,
  type ChangePasswordSchema,
} from "@/schemas/password.schema";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { NotificationBell } from "@/components/notifications";
import { useBrands, useDepartments, usePositions } from "@/hooks";
import {
  getBrandName,
  getDepartmentName,
  getPositionName,
} from "@/lib/organization-utils";
import { USER_ROLE_LABELS } from "@/shared/constants";

const mainItems = [
  { title: "Inicio", url: "/", icon: Home },
  { title: "Mi Aprendizaje", url: "/my-learning", icon: BookOpen },
  { title: "Mis Logros", url: "/achievements", icon: Trophy },
  { title: "Catálogo", url: "/courses", icon: BookOpen },
  { title: "Rutas", url: "/learning-paths", icon: Target },
  { title: "Certificados", url: "/my-certificate", icon: GraduationCap },
];

const adminItems = [
  { title: "Gestionar Cursos", url: "/admin/courses", icon: FolderTree },
  { title: "Gestionar Rutas", url: "/admin/paths", icon: Target },
  { title: "Gestionar Insignias", url: "/admin/badges", icon: Trophy },
  { title: "Usuarios", url: "/admin/users", icon: Users },
  { title: "Organización", url: "/admin/organization", icon: Building2 },
  { title: "Certificados", url: "/admin/certificate/all-users", icon: Users },
  { title: "Reportes", url: "/admin/reports", icon: BarChart3 },
];

const supervisorItems = [
  { title: "Usuarios", url: "/admin/users", icon: Users },
  { title: "Organización", url: "/admin/organization", icon: Building2 },
  { title: "Certificados", url: "/admin/certificate/users", icon: Users },
];

const contentManagerItems = [
  { title: "Gestionar Cursos", url: "/admin/courses", icon: FolderTree },
  { title: "Gestionar Rutas", url: "/admin/paths", icon: Target },
  { title: "Gestionar Insignias", url: "/admin/badges", icon: Trophy },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { user, logout, hasAnyRole } = useUser();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const changePasswordForm = useForm<ChangePasswordSchema>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleChangePasswordSubmit = async (data: ChangePasswordSchema) => {
    setIsChangingPassword(true);
    try {
      await authService.changePassword(data.currentPassword, data.newPassword);
      toast.success("Contraseña actualizada correctamente.");
      setIsChangePasswordOpen(false);
      changePasswordForm.reset();
    } catch (error: unknown) {
      // Extract the real API message from AxiosError.response.data.message
      let errorMsg = "Error al cambiar la contraseña.";
      if (axios.isAxiosError(error)) {
        errorMsg = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMsg = error.message;
      }

      // Map known API error messages to field-level errors
      if (
        errorMsg === "Incorrect password." ||
        errorMsg.toLowerCase().includes("incorrect password")
      ) {
        changePasswordForm.setError("currentPassword", {
          type: "server",
          message: "La contraseña actual es incorrecta.",
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Determine if user is admin/supervisor
  const isAdminUser = hasAnyRole([Roles.Admin]);
  const isSupervisorUser = hasAnyRole([Roles.Supervisor]);
  const isContentManagerUser = hasAnyRole([Roles.ContentManager]);
  const isEmployeeUser = hasAnyRole([Roles.Employee]);

  // Redirección automática según rol y ruta
  // Solo redirigir cuando la ruta sea exactamente /admin (entrada al panel)
  useEffect(() => {
    if (location.pathname !== "/admin") return;

    if (isAdminUser) {
      navigate("/admin/reports", { replace: true });
    } else if (isSupervisorUser) {
      navigate("/admin/users", { replace: true });
    } else if (isContentManagerUser) {
      navigate("/admin/courses", { replace: true });
    }
  }, [
    location.pathname,
    isAdminUser,
    isSupervisorUser,
    isContentManagerUser,
    navigate,
  ]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getNavClasses = (itemPath: string) => {
    if (itemPath === "/") {
      return location.pathname === "/"
        ? "bg-primary/5 text-primary font-medium border-l-2 border-primary"
        : "hover:bg-sidebar-accent transition-all duration-150 border-l-2 border-transparent";
    }

    const isActive = location.pathname.startsWith(itemPath);
    return isActive
      ? "bg-primary/5 text-primary font-medium border-l-2 border-primary"
      : "hover:bg-sidebar-accent transition-all duration-150 border-l-2 border-transparent";
  };

  const { data: brands = [], isLoading: brandsLoading } = useBrands();
  const { data: departments = [], isLoading: departmentsLoading } =
    useDepartments(Number(user.brandId));
  const { data: positions = [], isLoading: positionsLoading } = usePositions(
    Number(user.brandId),
    Number(user.departmentId),
  );

  const brandName = getBrandName(brands, Number(user.brandId));
  const departmentName = getDepartmentName(
    departments,
    Number(user.departmentId),
  );
  const positionName = getPositionName(positions, Number(user.positionId));

  const brandLogoMap: Record<string, string> = {
    teriyaki: "/assets/teriyaki_red.svg",
    temashi: "/assets/tamashi_red.svg",
    "jade restaurant": "/assets/jade_restaurant_red.svg",
  };

  const normalizedBrandName = (brandName || "").toLowerCase().trim();
  const logoSrc =
    brandLogoMap[normalizedBrandName] || "/assets/grupo-jade-red.svg";

  const isLogoReady =
    !brandsLoading && !departmentsLoading && !positionsLoading && !!brandName;

  return (
    <TooltipProvider delayDuration={0}>
      <Sidebar collapsible="icon" className={isCollapsed ? "w-36" : "w-64"}>
        {/* Header */}
        <SidebarHeader className="bg-sidebar border-b border-sidebar-border p-4 transition-all duration-200">
          <div className="flex shrink-0 items-center justify-center">
            {!isAdminUser ? (
              isLogoReady ? (
                <img
                  src={logoSrc}
                  alt="Grupo Jade Logo"
                  decoding="async"
                  className={`transition-all duration-200 ${
                    isCollapsed ? "h-20 w-16" : "h-24 w-44"
                  }`}
                />
              ) : (
                <div
                  className={`bg-muted animate-pulse rounded-lg ${
                    isCollapsed ? "h-20 w-16" : "h-24 w-44"
                  }`}
                />
              )
            ) : (
              <img
                src="/assets/grupo-jade-red.svg"
                alt="Grupo Jade Logo"
                decoding="async"
                className={`transition-all duration-200 ${
                  isCollapsed ? "h-20 w-16" : "h-24 w-44"
                }`}
              />
            )}
          </div>
        </SidebarHeader>

        {/* Content */}
        <SidebarContent>
          {/* Main Navigation - Only for Employees */}
          {isEmployeeUser && (
            <SidebarGroup>
              <SidebarGroupLabel>Navegación</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className={isCollapsed ? "ml-4" : ""}>
                  {mainItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild>
                            <NavLink
                              to={item.url}
                              className={getNavClasses(item.url)}
                            >
                              <item.icon className="h-5 w-5" />
                              {!isCollapsed && <span>{item.title}</span>}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent
                            side="right"
                            className="bg-popover text-popover-foreground"
                          >
                            <span>{item.title}</span>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Admin Navigation - según rol */}
          {isAdminUser && (
            <SidebarGroup>
              <SidebarGroupLabel>
                <Settings className="h-4 w-4 inline mr-2" />
                {!isCollapsed && "Administración"}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className={isCollapsed ? "ml-4" : ""}>
                  {adminItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild>
                            <NavLink
                              to={item.url}
                              className={getNavClasses(item.url)}
                            >
                              <item.icon className="h-5 w-5" />
                              {!isCollapsed && <span>{item.title}</span>}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent
                            side="right"
                            className="bg-popover text-popover-foreground"
                          >
                            <span>{item.title}</span>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
          {isSupervisorUser && !isAdminUser && (
            <SidebarGroup>
              <SidebarGroupLabel>
                <Settings className="h-4 w-4 inline mr-2" />
                {!isCollapsed && "Supervisión"}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className={isCollapsed ? "ml-4" : ""}>
                  {supervisorItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild>
                            <NavLink
                              to={item.url}
                              className={getNavClasses(item.url)}
                            >
                              <item.icon className="h-5 w-5" />
                              {!isCollapsed && <span>{item.title}</span>}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent
                            side="right"
                            className="bg-popover text-popover-foreground"
                          >
                            <span>{item.title}</span>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
          {isContentManagerUser && !isAdminUser && !isSupervisorUser && (
            <SidebarGroup>
              <SidebarGroupLabel>
                <Settings className="h-4 w-4 inline mr-2" />
                {!isCollapsed && "Gestión de Contenido"}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className={isCollapsed ? "ml-4" : ""}>
                  {contentManagerItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild>
                            <NavLink
                              to={item.url}
                              className={getNavClasses(item.url)}
                            >
                              <item.icon className="h-5 w-5" />
                              {!isCollapsed && <span>{item.title}</span>}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent
                            side="right"
                            className="bg-popover text-popover-foreground"
                          >
                            <span>{item.title}</span>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        {/* Footer */}
        <SidebarFooter
          className={`bg-sidebar border-t border-sidebar-border transition-all duration-200 ${
            isCollapsed ? "px-4 py-3" : "p-3 md:p-4"
          }`}
        >
          <div
            className={`flex items-center gap-2 mb-2 ${
              isCollapsed ? "justify-center" : ""
            }`}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size={isCollapsed ? "icon" : "default"}
                  className={`${
                    isCollapsed
                      ? "p-2 min-h-[44px] min-w-[44px]"
                      : "w-full min-h-[44px]"
                  } transition-all duration-150 text-destructive hover:bg-destructive/10 hover:text-destructive`}
                  onClick={handleLogout}
                >
                  <LogOut className="h-5 w-5" />
                  {!isCollapsed && <span className="ml-2">Cerrar Sesión</span>}
                </Button>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent
                  side="right"
                  className="bg-popover text-popover-foreground"
                >
                  <span>Cerrar Sesión</span>
                </TooltipContent>
              )}
            </Tooltip>
          </div>

          <div
            className={`flex items-center gap-2 ${
              isCollapsed ? "flex-col" : ""
            }`}
          >
            {/* Campana de notificaciones conectada al backend */}
            {!isAdminUser && <NotificationBell />}

            {/* Botón de perfil */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 min-h-[44px] min-w-[44px] transition-all duration-150 hover:bg-sidebar-accent text-sidebar-foreground"
                  onClick={() => setIsProfileModalOpen(true)}
                >
                  <User className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent
                  side="right"
                  className="bg-popover text-popover-foreground"
                >
                  <span>Perfil</span>
                </TooltipContent>
              )}
            </Tooltip>

            {!isCollapsed && user && (
              <div
                className="flex flex-col ml-2 min-w-0 cursor-pointer hover:opacity-80 transition-opacity duration-150"
                onClick={() => setIsProfileModalOpen(true)}
              >
                <span className="text-sm font-medium text-sidebar-foreground truncate">
                  {user.userName}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {USER_ROLE_LABELS[user.roles[0]] || "Usuario"}
                </span>
              </div>
            )}
          </div>
        </SidebarFooter>

        {/* User Profile Modal */}
        <Dialog open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-2xl">Perfil de Usuario</DialogTitle>
              <DialogDescription>
                Información detallada de tu cuenta
              </DialogDescription>

              <button
                type="button"
                onClick={() => setIsProfileModalOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-[#ef3f47] text-white transition-colors duration-200 shadow focus:outline-none focus:ring-2 focus:ring-[#ef3f47]"
                aria-label="Cerrar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Avatar and Name */}
              {user && (
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-full bg-gradient-primary flex items-center justify-center">
                    <User className="h-10 w-10 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{user.userName}</h3>
                    <p className="text-sm text-muted-foreground">
                      {user.roles
                        .map((role) => USER_ROLE_LABELS[role] || role)
                        .join(", ")}
                    </p>
                  </div>
                </div>
              )}

              <Separator />

              {/* User Details */}
              {user && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Correo Electrónico
                      </p>
                      <p className="text-sm font-medium">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <Settings className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Permisos</p>
                      <p className="text-sm font-medium">
                        {user.permissions.length} permisos asignados
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              {/* Stats */}
              {!isAdminUser && (
                <div className="space-y-4 px-3">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-[#ef3f47] bg-muted rounded-lg p-1" />
                    <div>
                      <p className="text-md font-bold text-primary">
                        {departmentName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Departamento
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-[#ef3f47] bg-muted rounded-lg p-1" />
                    <div>
                      <p className="text-md font-bold text-primary">
                        {positionName}
                      </p>
                      <p className="text-xs text-muted-foreground">Posición</p>
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              {/* Change Password Button */}
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => {
                  setIsProfileModalOpen(false);
                  changePasswordForm.reset();
                  setIsChangePasswordOpen(true);
                }}
              >
                <KeyRound className="h-4 w-4" />
                Cambiar Contraseña
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog
          open={isChangePasswordOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsChangePasswordOpen(false);
              changePasswordForm.reset();
              setShowCurrentPassword(false);
              setShowNewPassword(false);
              setShowConfirmPassword(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-[440px]">
            <DialogHeader>
              <DialogTitle>Cambiar Contraseña</DialogTitle>
              <DialogDescription>
                Introduce tu contraseña actual y la nueva contraseña.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={changePasswordForm.handleSubmit(handleChangePasswordSubmit)}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="cp-currentPassword">Contraseña Actual</Label>
                  <div className="relative">
                    <Input
                      id="cp-currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Introduce tu contraseña actual"
                      className="pr-11"
                      {...changePasswordForm.register("currentPassword")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors duration-200"
                      tabIndex={-1}
                      aria-label={showCurrentPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {changePasswordForm.formState.errors.currentPassword && (
                    <p className="text-sm text-destructive">
                      {changePasswordForm.formState.errors.currentPassword.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cp-newPassword">Nueva Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="cp-newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Mínimo 6 caracteres"
                      className="pr-11"
                      {...changePasswordForm.register("newPassword")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors duration-200"
                      tabIndex={-1}
                      aria-label={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {changePasswordForm.formState.errors.newPassword && (
                    <p className="text-sm text-destructive">
                      {changePasswordForm.formState.errors.newPassword.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cp-confirmPassword">
                    Repetir Nueva Contraseña
                  </Label>
                  <div className="relative">
                    <Input
                      id="cp-confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Repite la nueva contraseña"
                      className="pr-11"
                      {...changePasswordForm.register("confirmPassword")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors duration-200"
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {changePasswordForm.formState.errors.confirmPassword && (
                    <p className="text-sm text-destructive">
                      {changePasswordForm.formState.errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsChangePasswordOpen(false);
                    changePasswordForm.reset();
                    setShowCurrentPassword(false);
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  disabled={isChangingPassword}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isChangingPassword}>
                  {isChangingPassword && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Cambiar Contraseña
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </Sidebar>
    </TooltipProvider>
  );
}
